/**
 * Delivery estimate for the product page.
 *
 * The ported design shipped a hardcoded string ("Get it by Tomorrow if you order within
 * 2h 15m") that never changed — it said the same thing at 3am. This computes it for real:
 *
 *   - store-local clock  = visitor's UTC clock + the shop's UTC offset (rendered by Liquid)
 *   - before the cutoff on a business day  -> ships today
 *   - otherwise                            -> ships the next business day
 *   - arrival = ship date + lead business days (weekends skipped, no carrier holidays)
 *
 * Lead times are the two the store really sells (John, 2026-08-20): free
 * Standard, 2-3 business days, and Overnight at $199 for the next business day.
 *
 * The headline is "Arrives by" and it names the LATE end of the standard
 * window, not the early one. A family booking a service needs the date they can
 * count on; quoting the optimistic end and missing it by a day is the failure
 * this whole block exists to prevent. The full window follows in the same
 * sentence so nobody is misled into thinking it cannot come sooner.
 *
 * NOTE: whether the order day itself counts toward the window was asked and
 * never answered - the cutoff setting is how that is expressed here, and it is
 * an assumption until confirmed.
 *
 * The countdown is only shown when there is genuinely time left today. After the cutoff it
 * states the date with no timer, rather than inventing urgency.
 */
(function () {
  var DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // "-0400" -> -240 minutes
  function parseOffset(raw) {
    var m = /^([+-])(\d{2})(\d{2})$/.exec((raw || '').trim());
    if (!m) return null;
    var mins = parseInt(m[2], 10) * 60 + parseInt(m[3], 10);
    return m[1] === '-' ? -mins : mins;
  }

  // A Date whose UTC fields read as the store's wall clock, so all math below can use UTC
  // getters and never trip over the visitor's own timezone.
  function storeNow(offsetMin) {
    return new Date(Date.now() + offsetMin * 60000);
  }

  function isBusinessDay(d) {
    var wd = d.getUTCDay();
    return wd !== 0 && wd !== 6;
  }

  function addBusinessDays(from, n) {
    var out = new Date(from.getTime());
    while (n > 0) {
      out.setUTCDate(out.getUTCDate() + 1);
      if (isBusinessDay(out)) n--;
    }
    return out;
  }

  function sameDay(a, b) {
    return a.getUTCFullYear() === b.getUTCFullYear()
      && a.getUTCMonth() === b.getUTCMonth()
      && a.getUTCDate() === b.getUTCDate();
  }

  function label(arrive, now) {
    var tomorrow = new Date(now.getTime());
    tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
    if (sameDay(arrive, tomorrow)) return 'Tomorrow';
    return DAYS[arrive.getUTCDay()] + ', ' + MONTHS[arrive.getUTCMonth()] + ' ' + arrive.getUTCDate();
  }

  function remaining(ms) {
    var mins = Math.floor(ms / 60000);
    var h = Math.floor(mins / 60);
    var m = mins % 60;
    if (h > 0) return h + 'h ' + m + 'm';
    return m + 'm';
  }

  /** "Mon, Aug 18" — or a range collapsed to one date when both ends match. */
  function window_(ships, min, max, now) {
    var a = label(addBusinessDays(ships, min), now);
    var b = label(addBusinessDays(ships, max), now);
    return a === b ? a : a + ' – ' + b;
  }

  function setup(el) {
    var offset = parseOffset(el.getAttribute('data-store-offset'));
    if (offset === null) return;                 // leave the Liquid fallback text in place

    var cut = (el.getAttribute('data-cutoff') || '14:00').split(':');
    var cutH = parseInt(cut[0], 10);
    var cutM = parseInt(cut[1], 10) || 0;
    if (isNaN(cutH)) return;

    // The two rates the store actually sells: free Standard at 2-3 business
    // days, and Overnight at $199. Overnight is a single day rather than a
    // range, which window_ collapses to one date on its own.
    var sMin = parseInt(el.getAttribute('data-standard-min'), 10) || 2;
    var sMax = parseInt(el.getAttribute('data-standard-max'), 10) || 3;
    var xMin = parseInt(el.getAttribute('data-express-min'), 10) || 1;
    var xMax = parseInt(el.getAttribute('data-express-max'), 10) || 1;
    var showExpress = el.getAttribute('data-express') !== 'false';

    function render() {
      var now = storeNow(offset);
      var cutoff = new Date(now.getTime());
      cutoff.setUTCHours(cutH, cutM, 0, 0);

      var beforeCutoff = isBusinessDay(now) && now.getTime() < cutoff.getTime();

      var ships = new Date(now.getTime());
      if (!beforeCutoff) {
        do { ships.setUTCDate(ships.getUTCDate() + 1); } while (!isBusinessDay(ships));
      }

      // "Arrives by" is the date it is safe to plan around: the far end of the
      // standard window. The near end is shown after it, so the sentence is
      // both a commitment and the whole truth.
      var by = label(addBusinessDays(ships, sMax), now);
      var span = window_(ships, sMin, sMax, now);
      var html = 'Arrives by <strong>' + by + '</strong> with free shipping';
      if (span !== by) html += ' (' + span + ')';
      if (showExpress) {
        html += ', or <strong>' + window_(ships, xMin, xMax, now)
             + '</strong> with Overnight for $199';
      }
      // The countdown only earns its place while ordering today still moves the
      // ship date; after the cutoff it would be inventing urgency.
      if (beforeCutoff) {
        html += ' &mdash; order within <strong>'
             + remaining(cutoff.getTime() - now.getTime()) + '</strong> to ship today';
      }
      el.innerHTML = html;
    }

    render();
    setInterval(render, 30000);
  }

  var nodes = document.querySelectorAll('[data-delivery-estimate]');
  for (var i = 0; i < nodes.length; i++) setup(nodes[i]);
})();
